import { noop } from '@splunk/dashboard-utils';
import type { RefreshFunction } from '@splunk/dashboard-types';

const MIN_INTERVAL_SECONDS = 3; // 3 secs
// setTimeout uses a 32 bit int to store the timeout https://developer.mozilla.org/en-US/docs/Web/API/Window/setTimeout#maximum_delay_value
// This which means that it can fire immediately if the timeout is longer than 2^31 - 1 ms due to int overflow
// This corresponds to around ~25 days so it is easily hit if something is scheduled for the next scheduled time
// of a search that only runs every month
export const MAX_TIMEOUT_SECONDS = 12 * 60 * 60; // 12 hours

const computeInterval = (interval: number, refreshType: string) => {
    const constrainedInterval = Math.min(MAX_TIMEOUT_SECONDS, interval);

    return refreshType === 'delay'
        ? constrainedInterval
        : Math.max(MIN_INTERVAL_SECONDS, constrainedInterval);
};

/**
 * Control DataSource refresh behaviour
 */
export default class RefreshScheduler {
    refreshFunc: RefreshFunction;

    refreshType: string;

    interval: number;

    stopped: boolean;

    timeout: ReturnType<typeof setTimeout> | null;

    constructor({
        refreshFunc = noop,
        refreshInterval,
        refreshType = 'delay',
    }: {
        refreshFunc: RefreshFunction;
        refreshInterval: number;
        refreshType?: string;
    }) {
        this.refreshFunc = refreshFunc;
        this.refreshType = refreshType;
        this.interval = computeInterval(refreshInterval, refreshType);
        this.stopped = false;
        this.timeout = null;
    }

    scheduleNextRefresh = (): void => {
        // Do not schedule a new refresh if the timer has already started
        if (!this.timeout) {
            this.timeout = setTimeout(() => {
                this.timeout = null;
                if (!this.stopped) {
                    this.refreshFunc();
                }
            }, this.interval * 1000);
        }
    };

    stop = (): void => {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
        this.stopped = true;
    };

    start = (): void => {
        if (!this.stopped) {
            return;
        }

        this.stopped = false;
        this.scheduleNextRefresh();
    };
}
